/**
 * useSyncJobs Hooks
 *
 * React Query hooks for managing calendar sync jobs (the individual
 * remote-calendar-to-local-calendar pairings).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { api, SyncJob, SyncJobSettings } from '@/lib/api';
import { notifyCalendarDataChanged } from './useCalendarSyncLive';

/** Fetch all calendar sync jobs. Polls while any job is pending or syncing. */
export function useSyncJobs() {
  return useQuery<SyncJob[]>({
    queryKey: queryKeys.calendarSync.jobs(),
    queryFn: async () => {
      const res = await api.listCalendarSyncJobs();
      return res.jobs;
    },
    refetchInterval: (query) => {
      const jobs = query.state.data;
      if (!jobs) return false;
      const hasInProgress = jobs.some(
        (j) => j.lastSyncStatus === 'pending' || j.lastSyncStatus === 'syncing',
      );
      return hasInProgress ? 2000 : false;
    },
  });
}

/** Create a new sync job (pair a remote calendar with a local calendar). */
export function useCreateSyncJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      connectionId: string;
      localCalendarId: string;
      remoteCalendarId: string;
      remoteCalendarName?: string;
      // Set at creation rather than defaulted-then-edited: the user decides
      // these while choosing the calendar, and the route already accepts them.
      direction?: string;
      autoSyncEnabled?: boolean;
      conflictStrategy?: string;
    }) => {
      const res = await api.createCalendarSyncJob(data);
      return res.job;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarSync.jobs() });
    },
  });
}

/** Delete a sync job. */
export function useDeleteSyncJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, mode = 'keep' }: { jobId: string; mode?: 'keep' | 'remove' }) => {
      return api.deleteCalendarSyncJob(jobId, mode);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarSync.jobs() });
      // `mode=remove` soft-deletes events, so the calendar views are stale too.
      // They are local-state hooks rather than React Query, so nudge the same
      // refresh path the post-sync broadcast uses.
      notifyCalendarDataChanged();
    },
  });
}

/** Trigger an immediate sync for a job. */
export function useTriggerSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      const res = await api.triggerCalendarSync(jobId);
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarSync.jobs() });
    },
  });
}

/** Update settings (direction, auto-sync, conflict strategy) for a sync job. */
export function useUpdateSyncJobSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      settings,
    }: {
      jobId: string;
      settings: Partial<SyncJobSettings>;
    }) => {
      const res = await api.updateCalendarSyncJobSettings(jobId, settings);
      return res.job;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarSync.jobs() });
    },
  });
}
